import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaImage, 
  FaPhone,
  FaCalendarAlt,
  FaUser,
  FaTimes,
  FaSave,
  FaSearch,
  FaPaintBrush,
  FaEraser,
  FaUndo,
  FaRedo,
  FaCheck,
  FaEye
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './Orders.css';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState({
    nova: true,
    zaplatena: true,
    pripravena: true,
    caka_vyzdvihnutie: true,
    nevybavena: true,
    dorucena: false,
    zrusena: false,
    new: true,
    in_progress: true,
    completed: false,
    cancelled: false
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    phone_number: '',
    delivery_date: '',
    description: '',
    image_description: '',
    status: 'new'
  });
  
  // Image editor state
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingTool, setDrawingTool] = useState('pen');
  const [drawingColor, setDrawingColor] = useState('#FF0000');
  const [lineWidth, setLineWidth] = useState(2);
  const [drawingHistory, setDrawingHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);

  useEffect(() => {
    fetchOrders();
  }, []);

  // Load image when editor modal opens
  useEffect(() => {
    if (showImageEditor && uploadedImage) {
      loadImageToCanvas(uploadedImage);
    }
  }, [showImageEditor]);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      toast.error('Chyba pri načítaní objednávok');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name) {
      toast.error('Meno zákazníka je povinné');
      return;
    }

    try {
      const response = await api.post('/orders', formData);
      
      // Upload image if exists
      if (uploadedImage && response.data.id) {
        await api.post(`/orders/${response.data.id}/image`, {
          image: uploadedImage
        });
      }
      
      toast.success('Objednávka vytvorená');
      setFormData({
        customer_name: '',
        phone_number: '',
        delivery_date: '',
        description: '',
        image_description: '',
        status: 'new'
      });
      setUploadedImage(null);
      setShowAddForm(false);
      fetchOrders();
    } catch (error) {
      toast.error('Chyba pri vytváraní objednávky');
    }
  };

  const handleUpdate = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}`, formData);
      
      // Upload new image if changed
      if (uploadedImage) {
        await api.post(`/orders/${orderId}/image`, {
          image: uploadedImage
        });
      }
      
      toast.success('Objednávka aktualizovaná');
      setEditingOrder(null);
      setUploadedImage(null);
      fetchOrders();
    } catch (error) {
      toast.error('Chyba pri aktualizácii objednávky');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Naozaj chcete zmazať túto objednávku?')) {
      return;
    }

    try {
      await api.delete(`/orders/${orderId}`);
      toast.success('Objednávka zmazaná');
      fetchOrders();
    } catch (error) {
      toast.error('Chyba pri mazaní objednávky');
    }
  };

  // Image upload handler
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        // Don't automatically open editor
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Prosím vyberte platný obrázok');
    }
  };

  // Load image to canvas
  const loadImageToCanvas = (imageSrc) => {
    // Wait for next tick to ensure canvas is rendered
    setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.error('Canvas not found');
        return;
      }
      
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Scale image to fit canvas if too large
        const maxWidth = 800;
        const maxHeight = 600;
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        saveCanvasState();
      };
      
      img.onerror = () => {
        console.error('Failed to load image');
        toast.error('Chyba pri načítaní obrázka');
      };
      
      img.src = imageSrc;
    }, 100);
  };

  // Drawing functions
  const startDrawing = (e) => {
    if (!showImageEditor) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setIsDrawing(true);
    const ctx = canvas.getContext('2d');
    
    if (drawingTool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = lineWidth * 3;
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = drawingColor;
      ctx.lineWidth = lineWidth;
    }
    
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !showImageEditor) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      saveCanvasState();
    }
  };

  // Save canvas state for undo/redo
  const saveCanvasState = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const canvasData = canvas.toDataURL();
    const history = drawingHistory.slice(0, historyStep + 1);
    history.push(canvasData);
    setDrawingHistory(history);
    setHistoryStep(history.length - 1);
  };

  // Undo/Redo functions
  const handleUndo = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      setHistoryStep(newStep);
      loadImageToCanvas(drawingHistory[newStep]);
    }
  };

  const handleRedo = () => {
    if (historyStep < drawingHistory.length - 1) {
      const newStep = historyStep + 1;
      setHistoryStep(newStep);
      loadImageToCanvas(drawingHistory[newStep]);
    }
  };

  // Save edited image
  const saveEditedImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const editedImage = canvas.toDataURL('image/png');
    setUploadedImage(editedImage);
    setShowImageEditor(false);
    toast.success('Obrázok uložený');
  };

  // Component to load and display order image
  const OrderImage = ({ orderId }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [imageLoading, setImageLoading] = useState(true);
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
      const loadImage = async () => {
        try {
          const response = await api.get(`/orders/${orderId}/image`);
          if (response.data && response.data.image) {
            const img = response.data.image;
            setImageSrc(img.startsWith('data:') ? img : `data:image/png;base64,${img}`);
          } else {
            setImageError(true);
          }
        } catch (error) {
          console.error('Failed to load image:', error);
          setImageError(true);
        } finally {
          setImageLoading(false);
        }
      };
      loadImage();
    }, [orderId]);

    if (imageLoading) return <div>Načítavam obrázok...</div>;
    if (imageError) return <div>Chyba pri načítaní obrázka</div>;
    if (!imageSrc) return null;

    return <img src={imageSrc} alt="Order" className="order-detail-image" />;
  };

  // Filter orders
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          order.phone_number?.includes(searchTerm) ||
                          order.order_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatuses[order.status] === true;
    const matchesSource = filterSource === 'all' || order.source === filterSource;
    return matchesSearch && matchesStatus && matchesSource;
  });

  const getStatusColor = (status) => {
    switch(status) {
      // Manual statuses
      case 'new': return '#3b82f6';
      case 'in_progress': return '#f59e0b';
      case 'completed': return '#10b981';
      case 'cancelled': return '#ef4444';
      // Sashe statuses
      case 'nova': return '#3b82f6';
      case 'zaplatena': return '#22c55e';
      case 'pripravena': return '#f59e0b';
      case 'caka_vyzdvihnutie': return '#fb923c';
      case 'nevybavena': return '#ef4444';
      case 'dorucena': return '#10b981';
      case 'zrusena': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      // Manual statuses
      case 'new': return 'Nová';
      case 'in_progress': return 'Rozpracovaná';
      case 'completed': return 'Dokončená';
      case 'cancelled': return 'Zrušená';
      // Sashe statuses
      case 'nova': return 'Nová';
      case 'zaplatena': return 'Zaplatená';
      case 'pripravena': return 'Pripravená';
      case 'caka_vyzdvihnutie': return 'Čaká na vyzdvihnutie';
      case 'nevybavena': return 'Nevybavená';
      case 'dorucena': return 'Doručená';
      case 'zrusena': return 'Zrušená';
      default: return status;
    }
  };

  const getSourceLabel = (source) => {
    switch(source) {
      case 'sashe_monama': return 'Sashe Monama';
      case 'sashe_svadobka': return 'Sashe Svadobka';
      case 'monama_sk': return 'Monama.sk';
      case 'svadobka_sk': return 'Svadobka.sk';
      case 'monamadesign_com': return 'Monamadesign.com';
      case 'obchod': return 'Obchod';
      case 'manual': return 'Ručne';
      default: return source;
    }
  };

  const getSourceColor = (source) => {
    switch(source) {
      case 'sashe_monama': return '#ff6b6b';
      case 'sashe_svadobka': return '#ff1744';
      case 'monama_sk': return '#4ecdc4';
      case 'svadobka_sk': return '#f06292';
      case 'monamadesign_com': return '#45b7d1';
      case 'obchod': return '#95e1d3';
      case 'manual': return '#9ca3af';
      default: return '#6b7280';
    }
  };

  return (
    <div className="orders-page">
      <div className="page-header">
        <h1>Objednávky</h1>
        <motion.button
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaPlus /> Nová objednávka
        </motion.button>
      </div>

      {/* Source Filter Tabs */}
      <div className="source-filter-tabs">
        <button 
          className={`source-tab ${filterSource === 'all' ? 'active' : ''}`}
          onClick={() => setFilterSource('all')}
        >
          Všetky
        </button>
        <button 
          className={`source-tab ${filterSource === 'sashe_monama' ? 'active' : ''}`}
          onClick={() => setFilterSource('sashe_monama')}
        >
          Sashe Monama
        </button>
        <button 
          className={`source-tab ${filterSource === 'sashe_svadobka' ? 'active' : ''}`}
          onClick={() => setFilterSource('sashe_svadobka')}
        >
          Sashe Svadobka
        </button>
        <button 
          className={`source-tab ${filterSource === 'monama_sk' ? 'active' : ''}`}
          onClick={() => setFilterSource('monama_sk')}
        >
          Monama.sk
        </button>
        <button 
          className={`source-tab ${filterSource === 'svadobka_sk' ? 'active' : ''}`}
          onClick={() => setFilterSource('svadobka_sk')}
        >
          Svadobka.sk
        </button>
        <button 
          className={`source-tab ${filterSource === 'monamadesign_com' ? 'active' : ''}`}
          onClick={() => setFilterSource('monamadesign_com')}
        >
          Monamadesign.com
        </button>
        <button 
          className={`source-tab ${filterSource === 'obchod' ? 'active' : ''}`}
          onClick={() => setFilterSource('obchod')}
        >
          Obchod
        </button>
      </div>

      {/* Search and Filter */}
      <div className="search-filter-bar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Hľadať podľa mena, telefónu alebo čísla..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      {/* Status Filter Checkboxes */}
      <div className="status-filter-section">
        <h3>Filter podľa stavu:</h3>
        <div className="status-checkboxes">
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.nova}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, nova: e.target.checked})}
            />
            <span style={{color: getStatusColor('nova')}}>Nová</span>
          </label>
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.zaplatena}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, zaplatena: e.target.checked})}
            />
            <span style={{color: getStatusColor('zaplatena')}}>Zaplatená</span>
          </label>
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.pripravena}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, pripravena: e.target.checked})}
            />
            <span style={{color: getStatusColor('pripravena')}}>Pripravená</span>
          </label>
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.caka_vyzdvihnutie}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, caka_vyzdvihnutie: e.target.checked})}
            />
            <span style={{color: getStatusColor('caka_vyzdvihnutie')}}>Čaká na vyzdvihnutie</span>
          </label>
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.nevybavena}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, nevybavena: e.target.checked})}
            />
            <span style={{color: getStatusColor('nevybavena')}}>Nevybavená</span>
          </label>
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.dorucena}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, dorucena: e.target.checked})}
            />
            <span style={{color: getStatusColor('dorucena')}}>Doručená</span>
          </label>
          <label className="status-checkbox">
            <input
              type="checkbox"
              checked={selectedStatuses.zrusena}
              onChange={(e) => setSelectedStatuses({...selectedStatuses, zrusena: e.target.checked})}
            />
            <span style={{color: getStatusColor('zrusena')}}>Zrušená</span>
          </label>
        </div>
        <div className="status-actions">
          <button 
            className="btn-secondary"
            onClick={() => setSelectedStatuses({
              nova: true, zaplatena: true, pripravena: true, 
              caka_vyzdvihnutie: true, nevybavena: true, dorucena: true, 
              zrusena: true, new: true, in_progress: true, 
              completed: true, cancelled: true
            })}
          >
            Vybrať všetky
          </button>
          <button 
            className="btn-secondary"
            onClick={() => setSelectedStatuses({
              nova: false, zaplatena: false, pripravena: false, 
              caka_vyzdvihnutie: false, nevybavena: false, dorucena: false, 
              zrusena: false, new: false, in_progress: false, 
              completed: false, cancelled: false
            })}
          >
            Zrušiť výber
          </button>
          <button 
            className="btn-info"
            onClick={() => setSelectedStatuses({
              nova: true, zaplatena: true, pripravena: true, 
              caka_vyzdvihnutie: true, nevybavena: true, dorucena: false, 
              zrusena: false, new: true, in_progress: true, 
              completed: false, cancelled: false
            })}
          >
            Len aktívne
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {(showAddForm || editingOrder) && (
          <motion.div
            className="order-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <h2>{editingOrder ? 'Upraviť objednávku' : 'Nová objednávka'}</h2>
            <form onSubmit={editingOrder ? (e) => { e.preventDefault(); handleUpdate(editingOrder); } : handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label><FaUser /> Meno zákazníka *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FaPhone /> Telefón</label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label><FaCalendarAlt /> Dátum dodania</label>
                  <input
                    type="text"
                    placeholder="Dátum alebo text (napr. 'čo najskôr')"
                    value={formData.delivery_date}
                    onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Stav</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="new">Nová</option>
                    <option value="in_progress">Rozpracovaná</option>
                    <option value="completed">Dokončená</option>
                    <option value="cancelled">Zrušená</option>
                  </select>
                </div>
              </div>

              <div className="form-group full-width">
                <label>Popis objednávky</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  placeholder="Podrobný popis objednávky..."
                />
              </div>

              {/* Image Upload Section */}
              <div className="image-section">
                <label><FaImage /> Obrázok</label>
                <div className="image-controls">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    id="image-upload"
                    hidden
                  />
                  <label htmlFor="image-upload" className="btn btn-secondary">
                    <FaImage /> Nahrať obrázok
                  </label>
                  
                  {uploadedImage && (
                    <>
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => {
                          setShowImageEditor(true);
                          // Image will be loaded after modal is shown
                        }}
                      >
                        <FaPaintBrush /> Upraviť obrázok
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => setUploadedImage(null)}
                      >
                        <FaTrash /> Odstrániť
                      </button>
                    </>
                  )}
                </div>
                
                {uploadedImage && !showImageEditor && (
                  <div className="image-preview">
                    <img src={uploadedImage} alt="Preview" />
                  </div>
                )}
                
                <div className="form-group">
                  <label>Popis obrázka</label>
                  <input
                    type="text"
                    value={formData.image_description}
                    onChange={(e) => setFormData({ ...formData, image_description: e.target.value })}
                    placeholder="Popis toho, čo je na obrázku..."
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-success">
                  <FaSave /> {editingOrder ? 'Aktualizovať' : 'Vytvoriť'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingOrder(null);
                    setUploadedImage(null);
                    setFormData({
                      customer_name: '',
                      phone_number: '',
                      delivery_date: '',
                      description: '',
                      image_description: '',
                      status: 'new'
                    });
                  }}
                >
                  <FaTimes /> Zrušiť
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Editor Modal */}
      {showImageEditor && (
        <div className="image-editor-modal">
          <div className="editor-container">
            <div className="editor-header">
              <h3>Upraviť obrázok</h3>
              <button className="close-btn" onClick={() => setShowImageEditor(false)}>
                <FaTimes />
              </button>
            </div>
            
            <div className="editor-toolbar">
              <button
                className={`tool-btn ${drawingTool === 'pen' ? 'active' : ''}`}
                onClick={() => setDrawingTool('pen')}
              >
                <FaPaintBrush /> Pero
              </button>
              <button
                className={`tool-btn ${drawingTool === 'eraser' ? 'active' : ''}`}
                onClick={() => setDrawingTool('eraser')}
              >
                <FaEraser /> Guma
              </button>
              
              <input
                type="color"
                value={drawingColor}
                onChange={(e) => setDrawingColor(e.target.value)}
              />
              
              <label>
                Hrúbka:
                <input
                  type="range"
                  min="1"
                  max="20"
                  value={lineWidth}
                  onChange={(e) => setLineWidth(e.target.value)}
                />
              </label>
              
              <button className="tool-btn" onClick={handleUndo} disabled={historyStep <= 0}>
                <FaUndo /> Späť
              </button>
              <button className="tool-btn" onClick={handleRedo} disabled={historyStep >= drawingHistory.length - 1}>
                <FaRedo /> Vpred
              </button>
            </div>
            
            <div className="canvas-container">
              <canvas
                ref={canvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className="edit-canvas"
              />
            </div>
            
            <div className="editor-actions">
              <button className="btn btn-success" onClick={saveEditedImage}>
                <FaCheck /> Uložiť zmeny
              </button>
              <button className="btn btn-secondary" onClick={() => setShowImageEditor(false)}>
                <FaTimes /> Zrušiť
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders List */}
      <div className="orders-grid">
        {filteredOrders.map(order => (
          <motion.div
            key={order.id}
            className="order-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.02 }}
          >
            <div className="order-header">
              <div>
                <div className="order-number">#{order.order_number}</div>
                <div 
                  className="order-source"
                  style={{ backgroundColor: getSourceColor(order.source), opacity: 0.9 }}
                >
                  {getSourceLabel(order.source)}
                </div>
              </div>
              <div 
                className="order-status"
                style={{ backgroundColor: getStatusColor(order.status) }}
              >
                {getStatusText(order.status)}
              </div>
            </div>
            
            <div className="order-body">
              <h3>{order.customer_name}</h3>
              {order.phone_number && (
                <p className="order-phone">
                  <FaPhone /> {order.phone_number}
                </p>
              )}
              {order.delivery_date && (
                <p className="order-delivery">
                  <FaCalendarAlt /> Dodanie: {order.delivery_date}
                </p>
              )}
              {order.description && (
                <p className="order-description">{order.description}</p>
              )}
              {order.image_path && (
                <div className="order-image-indicator">
                  <FaImage /> Obsahuje obrázok
                </div>
              )}
            </div>
            
            <div className="order-footer">
              <div className="order-date">
                Vytvorené: {new Date(order.created_at).toLocaleDateString('sk-SK')}
              </div>
              <div className="order-actions">
                <button
                  className="btn-icon view"
                  onClick={() => setSelectedOrder(order)}
                  title="Zobraziť"
                >
                  <FaEye />
                </button>
                <button
                  className="btn-icon edit"
                  onClick={async () => {
                    setEditingOrder(order.id);
                    setFormData({
                      customer_name: order.customer_name,
                      phone_number: order.phone_number || '',
                      delivery_date: order.delivery_date || '',
                      description: order.description || '',
                      image_description: order.image_description || '',
                      status: order.status
                    });
                    // Load existing image if present
                    if (order.image_path) {
                      try {
                        const response = await api.get(`/orders/${order.id}/image`);
                        if (response.data && response.data.image) {
                          setUploadedImage(response.data.image);
                        }
                      } catch (error) {
                        console.error('Failed to load image:', error);
                      }
                    } else {
                      setUploadedImage(null);
                    }
                    setShowAddForm(true);
                  }}
                  title="Upraviť"
                >
                  <FaEdit />
                </button>
                <button
                  className="btn-icon delete"
                  onClick={() => handleDelete(order.id)}
                  title="Zmazať"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {filteredOrders.length === 0 && (
        <div className="no-orders">
          <p>Žiadne objednávky</p>
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="order-modal" onClick={() => setSelectedOrder(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detail objednávky #{selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <strong>Zákazník:</strong> {selectedOrder.customer_name}
              </div>
              {selectedOrder.phone_number && (
                <div className="detail-row">
                  <strong>Telefón:</strong> {selectedOrder.phone_number}
                </div>
              )}
              {selectedOrder.delivery_date && (
                <div className="detail-row">
                  <strong>Dodanie:</strong> {selectedOrder.delivery_date}
                </div>
              )}
              <div className="detail-row">
                <strong>Stav:</strong> 
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(selectedOrder.status) }}
                >
                  {getStatusText(selectedOrder.status)}
                </span>
              </div>
              {selectedOrder.description && (
                <div className="detail-row">
                  <strong>Popis:</strong>
                  <p>{selectedOrder.description}</p>
                </div>
              )}
              {selectedOrder.image_path && (
                <div className="detail-row">
                  <strong>Obrázok:</strong>
                  <OrderImage orderId={selectedOrder.id} />
                  {selectedOrder.image_description && (
                    <p className="image-description">{selectedOrder.image_description}</p>
                  )}
                </div>
              )}
              <div className="detail-row">
                <strong>Vytvorené:</strong> {new Date(selectedOrder.created_at).toLocaleString('sk-SK')}
              </div>
              {selectedOrder.updated_at && (
                <div className="detail-row">
                  <strong>Naposledy upravené:</strong> {new Date(selectedOrder.updated_at).toLocaleString('sk-SK')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading">Načítavam objednávky...</div>
      )}
    </div>
  );
};

export default Orders;